import React, { useCallback, useEffect, useRef } from 'react'
import style from './CarouselVideo.module.scss'
import { IEcommerceAnswerSchema, IEcommerceVideoAsset } from '../types'
import DevLog from '../../../modules/devlog/Module'
import useTimeline from '../../../modules/timeline/useTimeline'

interface ICarouselVideoProps {
  answer: IEcommerceAnswerSchema
  assetIndex: number
  asset: IEcommerceVideoAsset
  store: 'amazon' | 'walmart' | 'target' | 'default' | 'nike' | 'standart'
}

const CarouselVideo = ({ answer, assetIndex, asset, store }: ICarouselVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const timeline = useTimeline()

  const playFullscreen = useCallback(async () => {
    if (videoRef.current) {
      try {
        const video = videoRef.current
        await video.play()

        if (video.requestFullscreen) {
          await video.requestFullscreen()
        } else if ('webkitRequestFullscreen' in video) {
          await (video as HTMLVideoElement & { webkitRequestFullscreen(): Promise<void> }).webkitRequestFullscreen()
        } else if ('webkitEnterFullscreen' in video) {
          ;(video as HTMLVideoElement & { webkitEnterFullscreen(): void }).webkitEnterFullscreen()
        } else if ('msRequestFullscreen' in video) {
          await (video as HTMLVideoElement & { msRequestFullscreen(): Promise<void> }).msRequestFullscreen()
        }
      } catch (error) {
        DevLog._log(error, 'Error request fullscreen video.')

        try {
          if (videoRef.current) {
            await videoRef.current.play()
          }
        } catch (error) {
          DevLog._log(error, 'Error playing video.')
        }
      }
    }
  }, [])

  const onFullscreenChange = useCallback(() => {
    if (!document.webkitFullscreenElement && videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause()
    }
  }, [])

  const onVideoPause = () => {
    timeline.log({
      ref: answer.id,
      type: 'pause_video',
      target: assetIndex.toString()
    })
  }

  const onVideoPlay = () => {
    timeline.log({
      ref: answer.id,
      type: 'play_video',
      target: assetIndex.toString()
    })
  }

  useEffect(() => {
    document.addEventListener('fullscreenchange', onFullscreenChange)
    document.addEventListener('webkitfullscreenchange', onFullscreenChange)
    document.addEventListener('mozfullscreenchange', onFullscreenChange)
    document.addEventListener('MSFullscreenChange', onFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange)
      document.removeEventListener('mozfullscreenchange', onFullscreenChange)
      document.removeEventListener('MSFullscreenChange', onFullscreenChange)
    }
  }, [onFullscreenChange])

  return (
    <div
      className={style.carouselVideo}
      role="presentation"
      style={{ backgroundImage: `url(${asset.video.thumbnail})` }}
    >
      <button className={style[`${store}Play`]} onClick={() => void playFullscreen()}>
        <i className="pi pi-caret-right" />
        <span className={style.duration}>{formatDuration(asset.video.duration)}</span>
      </button>

      <video
        ref={videoRef}
        className={style.video}
        src={asset.video.url}
        preload="metadata"
        controls
        onPause={onVideoPause}
        onPlay={onVideoPlay}
      />
    </div>
  )
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = Math.floor(seconds % 60)

  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
}

export default CarouselVideo
